import { BlockWrapper, SourceLink, TitleWrapper, VideoContainer, VideoWrapper } from "./styles";
import { Image, Box, Block } from "../../../styles/basicStyles";
import { colors } from "../../../styles/colors";
import { FC } from "react";
import { BlogPostBlock } from "../../../models/blog";
import { useTranslation } from "next-i18next";
import Typography from "../../../components/typography";
import dynamic from "next/dynamic";
import Fade from "react-reveal/Fade";
const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

interface BlocksProps {
  block: BlogPostBlock;
}

const Blocks: FC<BlocksProps> = ({ block }) => {
  const { t, i18n: { language } } = useTranslation();

  const renderSource = (source?: string, animation?: 'bottom' | 'left' | 'right') => {
    if(!source) return null;

    return (
      <Fade bottom={animation === 'bottom'} left={animation === 'left'} right={animation === 'right'}>
        <Box fAlign='center'>
          <Typography
            as='div'
            fSize={1}
            lHeight={1.5}
            fWeight={400}
            mt={0.5}
            style={{ color: colors.davyGrey }}
          >
            {t('SOURCE')}:&nbsp;
          </Typography>
          <SourceLink
            href={block.source}
            target="_blank"
            rel="nofollow"
            aria-label={`Image source: ${block.source}`}
          >
            {block.source}
          </SourceLink>
        </Box>
      </Fade>
    );
  }

  return (
    <>
      {
        block.type === 1 ?
        <BlockWrapper
          mt={2.5}
          mb={1.563}
          fDirection={{ md: "row", xxs: "column" }}
        >
          <Block w={{ md: "30%", xxs: "100%" }}>
            {
              block.title && block.title[language] &&
              <Fade left>
                <TitleWrapper mr={{ md: 1.5, xxs: 0 }}>
                  <Typography
                    fSize={{ md: 1.875, xxs: 1.5 }}
                    lHeight={{ md: 1.938, xxs: 2 }}
                    fWeight={700}
                    style={{ color: colors.black, wordBreak: 'break-word' }}
                  >
                    {block.title[language]}
                  </Typography>
                </TitleWrapper>
              </Fade>
            }
          </Block>
          <Box mt={{ md: 0, xxs: 3.063 }} w={{ md: "70%", xxs: "100%" }}>
            {
              block.description && block.description[language] &&
              <Fade right>
                <Typography
                  fSize={1.125}
                  lHeight={1.75}
                  fWeight={400}
                  style={{ color: colors.black }}
                  dangerouslySetInnerHTML={{ __html: block.description[language]?.replace(/<p><\/p>/g, '<p>&nbsp;</p>') }}
                />
              </Fade>
            }
          </Box>
        </BlockWrapper>
        : 
        block.type === 2 ?
        <Box fJustify='center' fDirection='column' mt={1.563} mb={1.563}>
          <Fade bottom>
            <Image
              loading='lazy'
              src={block.image?.url}
              maxW="100%"
              w='auto'
              h='auto'
              alt='content image'
            />
          </Fade>
          {renderSource(block.source, 'bottom')}
        </Box>
        : 
        block.type === 3 ?
        <BlockWrapper
          mt={2.5}
          mb={1.563}
          fDirection={{ md: "row", xxs: "column" }}
        >
          <Box 
            display={{ md: block.text_orientation === 1 ? "flex" : "none", xxs: "none" }} 
            fDirection='column'
            pr={{ md: 2.188, xxs: 0 }} 
          >
            <Fade left>
              <Image 
                loading='lazy'
                src={block.image?.url} 
                maxW="100%" 
                alt='content image' 
              />
            </Fade>
            {renderSource(block.source, 'left')}
          </Box>
          <Fade left={block.text_orientation === 2} right={block.text_orientation === 1}>
            <Box fDirection="column">
              {
                block.title && block.title[language] &&
                <TitleWrapper
                  mr={{ md: 1.5, xxs: 0 }}
                  mb={1.875}
                >
                  <Typography
                    fSize={{ md: 1.875, xxs: 1.5 }}
                    lHeight={{ md: 1.938, xxs: 2 }}
                    fWeight={700}
                    style={{ color: colors.black, wordBreak: 'break-word' }}
                  >
                    {block.title[language]}
                  </Typography>
                </TitleWrapper>
              }
              {
                block.description && block.description[language] &&
                <Typography
                  fSize={1.125}
                  lHeight={1.75}
                  fWeight={400}
                  style={{ color: colors.black }}
                  dangerouslySetInnerHTML={{ __html: block.description[language]?.replace(/<p><\/p>/g, '<p>&nbsp;</p>') }}
                />
              }
            </Box>
          </Fade>
          <Box
            mt={{ md: 0, xxs: 1.25 }}
            fDirection='column'
            display={{ md: block.text_orientation === 2 ? "flex" : "none", xxs: "flex" }}
            pl={{ md: 2.188, xxs: 0 }} 
          >
            <Fade right>
              <Image 
                loading='lazy'
                src={block.image?.url} 
                maxW="100%" 
                alt='content image' 
              />
            </Fade>
            {renderSource(block.source, 'right')}
          </Box>
        </BlockWrapper>
        : 
        block.type === 4 ?
        <Box fJustify='center' fDirection='column' mt={1.563} mb={1.563}>
          <Fade bottom>
            <Image
              loading='lazy'
              src={`${process.env.NEXT_PUBLIC_API_FILES}${block.file?._id}`}
              maxW="100%"
              w='auto'
              h='auto'
              alt='content animation'
            />
          </Fade>
          {renderSource(block.source, 'bottom')}
        </Box>
        : 
        block.type === 5 ?
        <VideoWrapper
          fJustify={block.video_max_width ? "center" : "flex-start"}
          mb={1.563}
          mt={1.563}
        >
          <Fade bottom>
            <VideoContainer maxWidth={block.video_max_width}>
              <ReactPlayer
                url={block.file?._id ? `${process.env.NEXT_PUBLIC_API_FILES}${block.file?._id}` : block.video_url}
                controls
                width="100%"
                height="auto"
              />
            </VideoContainer>
          </Fade>
        </VideoWrapper>
        : 
        null
      }
    </>
  );
};

export default Blocks;
